import { useEffect, useState } from "react";
import { BarChart3, MousePointerClick, Puzzle, Target } from "lucide-react";
import api from "../lib/api";

/* Admin: IA → TravelerPulse ref-traffic dashboard (moderator/admin only). */

const RangeBtn = ({ d, days, setDays }) => (
  <button onClick={() => setDays(d)} data-testid={`ref-range-${d}`}
    className={`text-xs font-800 rounded-full px-3 py-1.5 border ${days === d ? "bg-blue-700 text-white border-blue-700" : "text-zinc-600 border-zinc-300 hover:border-blue-400"}`}>
    {d}d
  </button>
);

const TopTable = ({ title, rows, total, testid }) => (
  <div className="bg-white border border-zinc-200 rounded-2xl p-4" data-testid={testid}>
    <div className="text-xs font-800 uppercase text-zinc-400">{title}</div>
    {(!rows || rows.length === 0) ? (
      <p className="mt-2 text-xs text-zinc-400">No clicks recorded yet.</p>
    ) : (
      <div className="mt-2 space-y-1.5">
        {rows.map((r) => (
          <div key={r.name} className="flex items-center gap-2 text-xs">
            <span className="w-40 sm:w-52 truncate text-zinc-700 font-700" title={r.name}>{r.name}</span>
            <span className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
              <span className="block h-full bg-blue-600 rounded-full" style={{ width: `${(r.count / (total || 1)) * 100}%` }} />
            </span>
            <span className="w-10 text-right font-800 text-zinc-800">{r.count}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default function AdminRefTraffic() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "IA → TP Ref Traffic — Admin";
    api.get("/india/ref-stats", { params: { days } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load stats"));
  }, [days]);

  const maxDaily = Math.max(1, ...(data?.daily || []).map((d) => d.count));

  return (
    <div className="max-w-5xl mx-auto px-5 py-8" data-testid="admin-ref-traffic">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading font-900 text-3xl tracking-tight inline-flex items-center gap-2">
          <BarChart3 className="w-7 h-7 text-blue-700" /> IndianAirports → TravelerPulse traffic
        </h1>
        <div className="ml-auto flex gap-1.5">{[7, 30, 90].map((d) => <RangeBtn key={d} d={d} days={days} setDays={setDays} />)}</div>
      </div>
      <p className="mt-1 text-sm text-zinc-500">Every click on a ?ref=indianairports or partner-widget link is counted the moment the target page loads.</p>
      {err && <p className="mt-4 text-sm font-700 text-red-600" data-testid="ref-stats-error">{err}</p>}

      {data && (
        <>
          <div className="mt-5 grid sm:grid-cols-3 gap-4">
            <div className="bg-zinc-900 text-white rounded-2xl p-4">
              <MousePointerClick className="w-4 h-4 text-sky-400" />
              <div className="mt-1 font-heading font-900 text-3xl" data-testid="ref-total">{data.total}</div>
              <div className="text-[11px] text-zinc-400">funnel clicks · last {data.days} days</div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-2xl p-4">
              <Target className="w-4 h-4 text-blue-700" />
              <div className="mt-1 font-heading font-900 text-3xl">{data.by_target?.length || 0}</div>
              <div className="text-[11px] text-zinc-500">distinct TravelerPulse targets</div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-2xl p-4">
              <Puzzle className="w-4 h-4 text-orange-600" />
              <div className="mt-1 font-heading font-900 text-3xl">{data.by_campaign?.length || 0}</div>
              <div className="text-[11px] text-zinc-500">campaigns / partner domains</div>
            </div>
          </div>

          {/* Daily bars */}
          <div className="mt-4 bg-white border border-zinc-200 rounded-2xl p-4" data-testid="ref-daily">
            <div className="text-xs font-800 uppercase text-zinc-400">Daily clicks</div>
            {data.daily.length === 0 ? (
              <p className="mt-2 text-xs text-zinc-400">Nothing yet — clicks appear here as travelers use the promo strips, My Flight CTAs and partner widgets.</p>
            ) : (
              <div className="mt-3 flex items-end gap-[3px] h-24">
                {data.daily.map((d) => (
                  <div key={d.date} title={`${d.date}: ${d.count}`} className="flex-1 bg-blue-600 rounded-t"
                    style={{ height: `${Math.max(6, (d.count / maxDaily) * 100)}%` }} />
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <TopTable title="Top TravelerPulse targets" rows={data.by_target} total={data.total} testid="ref-by-target" />
            <TopTable title="Top source pages" rows={data.by_source} total={data.total} testid="ref-by-source" />
            <TopTable title="Campaigns / partners" rows={data.by_campaign} total={data.total} testid="ref-by-campaign" />
            <TopTable title="Medium" rows={data.by_medium} total={data.total} testid="ref-by-medium" />
          </div>
        </>
      )}
    </div>
  );
}
